import { Backdrop, Box, MenuItem, TextField, Typography } from '@mui/material';
import BackendService from 'BackendService';
import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { StyledButton } from '../../styles/StyledButtons';
import StyledModal from '../../styles/StyledModal';
import { StyledHeading } from '../../styles/styles';

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Reports are typically for a recent or current month, with some room either
// side for backfilling an older period or pre-dating a not-yet-closed one.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 16 }, (_, i) => CURRENT_YEAR + 1 - i);

interface IRRModalProps {
  open: boolean;
  fundOpportunityId: string;
  irrFile?: File;
  irrMonth?: string;
  isSubmitting: boolean;
  onClose: () => void;
  onFileChange: (file: File) => void;
  onMonthChange: (month: string) => void;
  onSubmittingChange: (submitting: boolean) => void;
  onShowToast: (message: string, type: 'error' | 'warning' | 'info' | 'success') => void;
}

const IRRModal: React.FC<IRRModalProps> = ({
  open,
  fundOpportunityId,
  irrFile,
  irrMonth,
  isSubmitting,
  onClose,
  onFileChange,
  onMonthChange,
  onSubmittingChange,
  onShowToast,
}) => {
  // Tracked separately from `irrMonth` (the combined "YYYY-MM" the parent
  // stores) so a user can pick one of Month/Year before the other without
  // the selection disappearing — the parent only receives a value once both
  // parts are set, which is also what keeps Submit disabled until then.
  const [monthPart, setMonthPart] = useState('');
  const [yearPart, setYearPart] = useState('');

  useEffect(() => {
    if (!open) return;
    const [y, m] = (irrMonth ?? '').split('-');
    setYearPart(y ?? '');
    setMonthPart(m ?? '');
    // Only re-sync when the modal opens: re-parsing on every `irrMonth`
    // change would fight the user's in-progress selection, since this
    // component is the one driving that prop in the first place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleMonthPartChange = (value: string) => {
    setMonthPart(value);
    onMonthChange(value && yearPart ? `${yearPart}-${value}` : '');
  };

  const handleYearPartChange = (value: string) => {
    setYearPart(value);
    onMonthChange(monthPart && value ? `${value}-${monthPart}` : '');
  };

  const handleSubmit = () => {
    if (!irrFile || !irrMonth) return;

    const formData = new FormData();
    formData.append('file', irrFile);
    formData.append('name', irrFile.name);
    formData.append('month', irrMonth);

    onSubmittingChange(true);

    new BackendService()
      .submitIRRUpdateCsv(fundOpportunityId, formData)
      .then(() => {
        onSubmittingChange(false);
        onClose();
        onShowToast('CSV uploaded and processed successfully', 'success');
      })
      .catch((error) => {
        console.error('Error uploading CSV', error);
        onSubmittingChange(false);
        onShowToast('Error uploading or processing CSV', 'error');
      });
  };

  return (
    <StyledModal
      tone="tone1"
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}
    >
      <StyledHeading headerVariant="subheading">Select Reporting Month</StyledHeading>

      <Dropzone
        onDrop={(acceptedFiles) => {
          const file = acceptedFiles[0];
          onFileChange(file);
        }}
        accept={{
          'text/csv': ['.csv'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.ms-excel': ['.xls'],
        }}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              mb: 2,
            }}
          >
            <input {...getInputProps()} />
            {irrFile ? (
              <Typography variant="body2">Selected file: {irrFile.name}</Typography>
            ) : (
              <Typography variant="body2">
                {isDragActive
                  ? 'Drop the file here...'
                  : 'Drag and drop a CSV/XLSX file here, or click to select'}
              </Typography>
            )}
          </Box>
        )}
      </Dropzone>

      <Typography variant="body2" sx={{ mb: 1 }}>
        Which month does this report cover?
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
        <TextField
          select
          label="Month"
          value={monthPart}
          onChange={(e) => handleMonthPartChange(e.target.value)}
          fullWidth
        >
          <MenuItem value="">
            <em>Select month</em>
          </MenuItem>
          {MONTH_OPTIONS.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Year"
          value={yearPart}
          onChange={(e) => handleYearPartChange(e.target.value)}
          fullWidth
        >
          <MenuItem value="">
            <em>Select year</em>
          </MenuItem>
          {YEAR_OPTIONS.map((year) => (
            <MenuItem key={year} value={String(year)}>
              {year}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', minHeight: '1.25em' }}>
        {monthPart && yearPart
          ? `Reporting period: ${MONTH_OPTIONS.find((month) => month.value === monthPart)?.label} ${yearPart}`
          : ' '}
      </Typography>
      <StyledButton onClick={handleSubmit} disabled={isSubmitting || !irrFile || !irrMonth}>
        Submit
      </StyledButton>
    </StyledModal>
  );
};

export default IRRModal;
